import React from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  submitReport,
  resolveReport,
  escalateReport,
} from "@/api/fetch";
import DakotaAiIcon from "@/assets/images/dakota-ai-icon.png"
import { captureEvent } from "@/utils/posthog";
import { useTranslation } from "react-i18next";
import { ExamDialogContent } from "./ExamDialogContent";

type FlowState =
  | "idle"
  | "loading"
  | "ai_response"
  | "submitted"
  | "resolving"
  | "resolved"
  | "escalating"
  | "escalated";

interface QuestionReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  question: Record<string, any>;
  selectedAnswer?: number;
  email?: string;
}

/**
 * Multi-step report modal with AI triage.
 * Flow: form → loading → AI response (or submitted) → resolve/escalate → submitted
 */
export function QuestionReportModal({
  open,
  question,
  selectedAnswer,
  onOpenChange,
  email: userEmail,
}: QuestionReportModalProps) {
  const { t } = useTranslation();
  const [error, setError] = React.useState("");
  const [resources, setResources] = React.useState("");
  const [flowState, setFlowState] = React.useState<FlowState>("idle");
  const [submitError, setSubmitError] = React.useState("");
  const [aiResponse, setAiResponse] = React.useState("");
  const [reportId, setReportId] = React.useState("");

  const isIdle = flowState === "idle";
  const isLoading = flowState === "loading";
  const isResolving = flowState === "resolving";
  const isEscalating = flowState === "escalating";
  const isFinished = flowState === "resolved" || flowState === "escalated";
  const isAutoClose = flowState === "submitted";
  const isProcessing = isResolving || isEscalating;
  const showAiView = flowState === "ai_response" || isProcessing || isFinished;
  const showForm = isIdle || isLoading || flowState === "submitted";

  const resetState = React.useCallback(() => {
    setError("");
    setResources("");
    setFlowState("idle");
    setSubmitError("");
    setAiResponse("");
    setReportId("");
  }, []);

  const handleClose = React.useCallback(() => {
    resetState();
    onOpenChange(false);
  }, [resetState, onOpenChange]);

  React.useEffect(() => {
    if (!isAutoClose) return;
    const timer = setTimeout(handleClose, 2000);
    return () => clearTimeout(timer);
  }, [isAutoClose, handleClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!error || !isIdle) return;

    try {
      setSubmitError("");
      setFlowState("loading");

      const result = await submitReport({
        question,
        error,
        code_used: resources || undefined,
        email: userEmail,
      });

      setReportId(result.reportId);

      captureEvent("question_reported", {
        question: question?.question,
        questionId: question?.question_id,
        selected_answer: question?.["answer_" + selectedAnswer],
        correct_answer: question?.["correct_answer"],
        triage_result: result.status,
      });

      if (result.status === "auto_respond" && result.aiResponse) {
        setAiResponse(result.aiResponse);
        setFlowState("ai_response");
      } else {
        setFlowState("submitted");
      }
    } catch {
      setSubmitError(t("test.report_unable_submit"));
      setFlowState("idle");
    }
  };

  const handleResolve = async () => {
    setFlowState("resolving");
    try {
      await resolveReport(reportId);
    } catch {
      // Best-effort
    }
    setFlowState("resolved");
  };

  const handleEscalate = async () => {
    setFlowState("escalating");
    try {
      await escalateReport(reportId);
    } catch {
      // Best-effort
    }
    setFlowState("escalated");
  };

  const formFooter = showForm ? (
    <>
      <button
        className="h-9 rounded-lg border border-[#e0e0e0] px-4 text-sm font-semibold leading-5 text-[#0a0c11] hover:bg-[#f4f4f6]"
        onClick={handleClose}
        type="button"
      >
        {t("common.cancel")}
      </button>
      <button
        className="h-9 rounded-lg bg-[var(--dp-brand-button)] px-4 text-sm font-semibold leading-5 text-white hover:bg-[var(--dp-brand-hover)] disabled:bg-[var(--dp-brand-button)] disabled:opacity-100"
        disabled={!error || !isIdle}
        form="question-report-form"
        type="submit"
      >
        {isLoading
          ? t("test.report_checking_kb")
          : flowState === "submitted"
            ? t("test.report_submitted")
            : t("test.report_check_submit")}
      </button>
    </>
  ) : undefined;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <ExamDialogContent
        className="max-h-[90vh] overflow-auto"
        footer={formFooter}
        title={t("test.report_modal_title")}
      >
        {showAiView && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold">{t("test.report_reported_error")}</p>
              <p className="mt-1 text-sm italic leading-relaxed text-gray-600">
                {error}
              </p>
            </div>

            <div className="rounded-2xl bg-[#fff8e8] p-4">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold">
                  {t("test.report_dakota_ai_label")}
                </p>
                <img src={DakotaAiIcon} alt={t("test.report_dakota_ai_alt")} />
              </div>
              <p className="text-sm leading-relaxed text-gray-800">
                {aiResponse}
              </p>
            </div>

            <Button
              onClick={handleResolve}
              disabled={isFinished || isProcessing}
              className={`h-12 w-full rounded-full py-3 text-white ${
                isFinished || isProcessing
                  ? "bg-[#EBECF0] text-gray-500 disabled:opacity-100"
                  : "bg-[var(--dp-brand-button)] hover:bg-[var(--dp-brand-hover)]"
              }`}
            >
              {isResolving ? t("test.report_resolving") : t("test.report_mark_resolved")}
            </Button>

            <Button
              onClick={handleEscalate}
              disabled={isFinished || isProcessing}
              variant={flowState === "escalated" ? "default" : "outline"}
              className={`h-12 w-full rounded-full py-3 ${
                flowState === "escalated"
                  ? "border-0 bg-[#8C929C] text-white disabled:opacity-100"
                  : isFinished || isProcessing
                    ? "border-2 border-gray-300 text-gray-400 disabled:opacity-100"
                    : "border-2 border-[var(--dp-brand-button)] text-[var(--dp-brand-button)] hover:bg-[var(--dp-brand-soft)]"
              }`}
            >
              {flowState === "escalated"
                ? t("test.report_submitted")
                : isEscalating
                  ? t("test.report_submitting")
                  : t("test.report_submit_anyway")}
            </Button>
          </div>
        )}

        {showForm && (
          <form className="space-y-4" id="question-report-form" onSubmit={handleSubmit}>
            {submitError && (
              <p className="text-sm text-red-500">{submitError}</p>
            )}

            <div className="space-y-1.5">
              <label htmlFor="error" className="block text-sm font-semibold leading-normal text-[#0a0c11]">
                {t("test.report_field_error_label")}*
              </label>
              <Textarea
                className="h-[93px] min-h-[93px] resize-none rounded-lg border-[#efefef] bg-white p-4 text-base leading-6 shadow-none placeholder:text-[#8c929c] focus-visible:ring-[var(--dp-brand-button)]"
                disabled={!isIdle}
                id="error"
                onChange={(e) => setError(e.target.value)}
                placeholder={t("test.report_error_placeholder")}
                required
                value={error}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="resources" className="block text-sm font-semibold leading-normal text-[#0a0c11]">
                {t("test.report_field_resources_label")}
              </label>
              <Input
                className="h-11 rounded-lg border-[#efefef] bg-white px-3 text-sm leading-5 shadow-none placeholder:text-[#8c929c] focus-visible:ring-[var(--dp-brand-button)]"
                disabled={!isIdle}
                id="resources"
                onChange={(e) => setResources(e.target.value)}
                placeholder={t("test.report_resources_placeholder")}
                value={resources}
              />
            </div>
          </form>
        )}
      </ExamDialogContent>
    </Dialog>
  );
}
